import type { ApplianceType } from 'src/helper/shared-types';
import type { VendorListLevel } from 'src/redux/vendor-preferences/types';

type PropertyMarket = {
  id: string;
  name: string;
};

export type PropertyListItem = {
  property_uuid: string;
  full_address: string;
  property_name: string | null;
  property_type: string | null;
  street_address: string;
  unit_number: string | null;
  city: string;
  state_code: string | null;
  postal_zip_code: string;
  latitude: string | null;
  longitude: string | null;
  last_service_at: string | null;
  open_sr_count: number;
  property_manager: string | null;
  market: PropertyMarket | null;
};

export type PropertyListMeta = {
  next: string | null;
  prev: string | null;
  per_page: number;
  last_synced_at: string | null;
  unassigned_properties_count?: number;
};

export type PropertyDetailsLabelValue = {
  label: string | null;
  value: string | null;
};

export type PropertyDetailsAccessPet = {
  label: string;
  value: string;
};

export type PropertyDetailsAccessPets = {
  selected: PropertyDetailsAccessPet[];
  other_detail: string | null;
};

export type PropertyDetailsAccessInformation = {
  access_method: PropertyDetailsLabelValue;
  access_code: string | null;
  access_note: string | null;
  pets: PropertyDetailsAccessPets;
  access_code_history_count: number;
};

export type PropertyDetailsPrimaryResident = {
  resident_id: string;
  first_name: string | null;
  last_name: string | null;
  phone_number: string | null;
  email: string | null;
};

export type PropertyDetailsManager = {
  property_manager_id: string;
  company_name: string | null;
  first_name: string | null;
  last_name: string | null;
  phone_number: string | null;
  email: string | null;
  is_company: boolean;
};

export type PropertyDetailsQuoteApprovalContact = {
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
};

export type PropertyDetailsOwnerContact = {
  property_owner_uuid: string;
  relationship_type: 'owner' | 'landlord';
  company_name: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  receives_quote_approvals: boolean;
  managed_by: 'external_connection' | 'foresight';
  party_kind: 'individual' | 'organization' | null;
  organization_type: 'llc' | 'fund' | 'other' | null;
  quote_approval_contact: PropertyDetailsQuoteApprovalContact | null;
};

export type PropertyAddressVerificationIssue = {
  code: string;
  message: string;
};

export type PropertyDetails = {
  property_uuid: string;
  property_name: string | null;
  full_address: string;
  street_address: string;
  unit_number: string | null;
  city: string;
  state_code: string;
  state_name: string;
  postal_zip_code: string;
  latitude: number | null;
  longitude: number | null;
  address_verified?: boolean;
  address_verification_issue?: PropertyAddressVerificationIssue | null;
  property_type: string | null;
  bed_unit: string | null;
  bed_count: number | null;
  bath_count: number | null;
  square_feet: number | null;
  lot_size_square_feet: number | null;
  year_built: number | null;
  estimated_value: number | null;
  sale_price: number | null;
  source: string | null;
  marketplace_synced_at: string | null;
  primary_resident: PropertyDetailsPrimaryResident | null;
  property_manager: PropertyDetailsManager | null;
  owner_contacts: PropertyDetailsOwnerContact[];
  access_information: PropertyDetailsAccessInformation | null;
  open_sr_count: number;
  total_wo_count: number;
  total_resident_count: number;
  overridden_vendors_count?: number | null;
  market: PropertyMarket | null;
};

export type PropertyDetailsResponse = {
  data: PropertyDetails;
};

export type PropertyResidentStatus = 'Current' | 'Past' | 'Notice' | 'Eviction';

export type PropertyResident = {
  resident_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone_number: string | null;
  formatted_phone_number: string | null;
  is_primary_resident: boolean;
  /**
   * Retained from the API response to match the server shape.
   * The Figma design for the Residents tab does not include a Status column,
   * so this field is intentionally not rendered in the UI.
   */
  status: PropertyResidentStatus | string | null;
};

export type PropertyResidentsMeta = {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
};

export type PropertyResidentsState = {
  ids: string[];
  source: { [residentUuid: string]: PropertyResident };
  meta: PropertyResidentsMeta | null;
  isLoading: boolean;
  hasFetched: boolean;
  error: string | null;
};

/**
 * Service requests scoped to a property — response shape mirrors the trimmed
 * payload returned by `GET /api/properties/{uuid}/service-requests` (ORI-162).
 * Intentionally NOT re-using `src/redux/service-request/types.ts` because the
 * BE intentionally returns a slimmer object here.
 */
export type PropertyServiceRequestStatus = {
  label: string;
  value: string;
  color_class: string;
};

export type PropertyServiceRequestPriority = {
  label: string;
  value: string;
};

export type PropertyServiceRequestCategory = {
  label: string;
  value: string;
};

export type PropertyServiceRequestAssignee = {
  user_id: string;
  name: string;
  profile_pic: string | null;
};

export type PropertyServiceRequest = {
  service_request_id: string;
  service_request_number: string;
  status: PropertyServiceRequestStatus | null;
  categories: PropertyServiceRequestCategory[];
  assignees: PropertyServiceRequestAssignee[];
  priority: PropertyServiceRequestPriority | null;
  due_date: string | null;
  created_at: string;
};

export type PropertyServiceRequestsMeta = {
  prev: string | null;
  next: string | null;
  last: string | null;
  total: number;
  per_page: number;
  current_page: number;
  from: number | null;
  to: number | null;
};

export type PropertyServiceRequestsState = {
  ids: string[];
  source: { [serviceRequestUuid: string]: PropertyServiceRequest };
  meta: PropertyServiceRequestsMeta | null;
  isLoading: boolean;
  hasFetched: boolean;
  error: string | null;
};

export type AccessCodeHistoryEntry = {
  id: string;
  code: string;
  access_method: { label: string | null; value: string };
  updated_by: string;
  changed_at: string;
  is_latest: boolean;
  is_current: boolean;
};

export type AccessCodeHistoryMeta = {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
};

export type AccessCodeHistoryState = {
  ids: string[];
  source: { [accessCodeHistoryId: string]: AccessCodeHistoryEntry };
  meta: AccessCodeHistoryMeta | null;
  isLoading: boolean;
  hasFetched: boolean;
  error: string | null;
};

export type PropertyMoreDetailsConstructionExterior = {
  year_built: number | null;
  condition: string | null;
  construction_type: string | null;
  frame_type: string | null;
  wall_type: string | null;
  foundation_type: string | null;
  roof_cover: string | null;
  roof_shape: string | null;
  building_shape: string | null;
  garage_type: string | null;
  parking_type: string | null;
  garage_size: string | null;
  parking_spaces: number | null;
  driveway: string | null;
  porch_deck: string | null;
  pool_type: string | null;
  pool: boolean | null;
  fence: boolean | null;
};

export type PropertyMoreDetailsInteriorUtilities = {
  bed_count: number | null;
  bath_count: number | null;
  square_feet: number | null;
  stories: number | null;
  rooms_total: number | null;
  basement_type: string | null;
  basement_finished_percentage: number | null;
  fireplace_count: number | null;
  cooling_type: string | null;
  heating_type: string | null;
  heating_fuel: string | null;
  energy_type: string | null;
  water_type: string | null;
  sewer_type: string | null;
  electrical_service: string | null;
};

export type PropertyMoreDetailsLotLegal = {
  lot_size_square_feet: number | null;
  lot_dimensions: string | null;
  lot_number: string | null;
  lot_type: string | null;
  zoning: string | null;
  legal_description: string | null;
  deed_type: string | null;
  apn: string | null;
};

export type PropertyMoreDetails = {
  property_uuid: string;
  construction_exterior: PropertyMoreDetailsConstructionExterior;
  interior_utilities: PropertyMoreDetailsInteriorUtilities;
  lot_legal: PropertyMoreDetailsLotLegal;
  source: string | null;
  synced_at: string | null;
};

export type PropertyMoreDetailsState = {
  data: PropertyMoreDetails | null;
  isLoading: boolean;
  hasFetched: boolean;
  error: string | null;
};

export type PropertyMechanicalsState = {
  ids: string[];
  source: { [applianceId: string]: ApplianceType };
  isLoading: boolean;
  hasFetched: boolean;
  error: string | null;
};

// ── Preferred Vendors (property detail tab) ──────────────────────────────────
// The tab is a master–detail UI and only ever shows ONE category's detail at a
// time, so the state mirrors that exactly:
//   • `list`   — the left-hand category rows (name + preview), from the list endpoint.
//   • `detail` — the single open category's full data, from the detail endpoint.
// Vendor order is positional: a vendor list is stored in rank order, so rank is
// always `index + 1` and drag-reorder is a single array move. Only one property
// detail page is open at a time, so this lives as a single flat state on the
// slice (not keyed by property).

export type PVVendor = { vendor_id: string; name: string };

export type PVEligibleVendor = PVVendor & {
  is_market_preferred: boolean;
  /** True → vendor can service every subcategory in the category. */
  has_all_skills: boolean;
  /** Subcategory ids the vendor can service (only meaningful when !has_all_skills). */
  skills: string[];
};

/**
 * Whether a vendor may appear under a subcategory. A vendor missing from the
 * eligible map (fetch not landed yet, or a ranked vendor the backend no longer
 * returns) is treated as eligible so it stays visible.
 */
export const isVendorEligibleForSub = (
  vendor: PVEligibleVendor | undefined,
  subcategoryId: string,
): boolean => !vendor || vendor.has_all_skills || vendor.skills.includes(subcategoryId);

export type PVCategoryOption = { value: string; label: string };

/**
 * Signals why the "Add New Category" search has nothing to offer, so the UI
 * can show a specific reason instead of a generic empty list.
 * `null` → categories are available to search normally.
 */
export type PVCategorySearchState =
  | 'no_categories_configured'
  | 'no_eligible_vendors'
  | 'all_categories_overridden'
  | null;

/** A property's overridden category — a row in the left-hand category list. */
export type PVOverriddenCategory = {
  id: string;
  name: string;
  /** Vendor names for the preview line, shown until/unless detail provides live names. */
  previewNames: string[];
};

export type PVDetailSubCategory = {
  subcategory_id: string;
  name: string;
  /** false → inherits the category default; true → customized. */
  is_override: boolean;
  /** Overridden: own list. Inheriting: server's pre-filtered defaults, shown until the eligible map loads. */
  vendors: PVVendor[];
  /** The level this sub-trade's list comes from (6.6); null when the server doesn't say. */
  source: VendorListLevel | null;
};

/** The single open category's full detail (from the detail / add endpoints). */
export type PVCategoryDetailState = {
  category_id: string;
  name: string;
  marketDefaultLabel: string;
  /** The level the trade's list comes from (6.6); null when the server doesn't say. */
  defaultSource: VendorListLevel | null;
  /** Category default vendors in rank order. */
  defaultVendors: PVVendor[];
  subCategories: PVDetailSubCategory[];
};

/**
 * The selected category's full eligible-vendor list, fetched once per category.
 * Subcategory eligibility is derived on demand from each vendor's `skills` /
 * `has_all_skills` via `isVendorEligibleForSub` — no precomputed lookups stored.
 */
export type EligibleVendorsState = {
  /** Distinguishes "not loaded yet" from "loaded empty"; also the loading signal. */
  hasFetched: boolean;
  vendorsById: Record<string, PVEligibleVendor>;
  /** Server order — drives picker option order. */
  allVendorIds: string[];
};

export const createEligibleVendorsState = (): EligibleVendorsState => ({
  hasFetched: false,
  vendorsById: {},
  allVendorIds: [],
});

export type PropertyPreferredVendorsState = {
  /** The property's overridden categories (rows with a preferred-vendor override). */
  overriddenCategories: PVOverriddenCategory[];
  /** Full catalog in API order; "available to add" is derived from this minus `overriddenCategories`. */
  categories: PVCategoryOption[];
  categorySearchState: PVCategorySearchState;
  selectedCategoryId: string | null;
  detail: PVCategoryDetailState | null;
  isLoadingList: boolean;
  isLoadingDetail: boolean;
  hasFetched: boolean;
  /** True when the last list fetch failed (not cancelled); the tab shows the failed state. */
  listFailed: boolean;
  /** True when the current list data was fetched with a search filter. */
  isListFiltered: boolean;
  /** True when the open category's detail fetch failed; drives the failed-pane UI. */
  detailFailed: boolean;
  /** The selected category's eligible vendors, normalized for O(1) lookups. */
  eligibleVendors: EligibleVendorsState;
};

export const createPreferredVendorsState = (): PropertyPreferredVendorsState => ({
  overriddenCategories: [],
  categories: [],
  categorySearchState: null,
  selectedCategoryId: null,
  detail: null,
  isLoadingList: false,
  isLoadingDetail: false,
  hasFetched: false,
  listFailed: false,
  isListFiltered: false,
  detailFailed: false,
  eligibleVendors: createEligibleVendorsState(),
});

// ── Preferred Vendors API response shapes (match the documented contract) ─────

export type ApiRankedVendor = { vendor_id: string; name: string; rank: number };
export type ApiVendor = { vendor_id: string; name: string };
export type ApiLabelValue = { label: string; value: string };

export type PreferredVendorsListResponse = {
  categories: ApiLabelValue[];
  override_categories: Array<{
    label: string;
    value: string;
    preferred_vendors: string[];
  }>;
  category_search_state: PVCategorySearchState;
  propertyId?: string;
};

export type ApiSubCategoryDetail = {
  label: string;
  value: string;
  is_inherit: boolean;
  /** property | market | company: where this sub-trade's list comes from (6.6). Additive. */
  source?: VendorListLevel | null;
  vendors: ApiRankedVendor[];
};

export type CategoryPreferredVendorsResponse = {
  category: ApiLabelValue;
  default_vendors: ApiRankedVendor[];
  /** property | market | company: where the trade's list comes from (6.6). Additive. */
  default_source?: VendorListLevel | null;
  market_default_label?: string;
  sub_categories: ApiSubCategoryDetail[];
  propertyId?: string;
  category_id?: string;
};

/** Single eligible vendor from the per-category `GET .../eligible-vendors` route. */
export type ApiEligibleVendor = {
  vendor_id: string;
  name: string;
  is_market_preferred: boolean;
  has_all_skills?: boolean;
  skills?: string[];
};

/** The route returns the full eligible list as a bare array. */
export type EligibleVendorsResponse = ApiEligibleVendor[];

export type AddOverrideCategoryResponse = {
  category: ApiLabelValue;
  default_vendors: ApiRankedVendor[];
  default_source?: VendorListLevel | null;
  sub_categories: ApiSubCategoryDetail[];
  propertyId?: string;
};

export type RemoveOverrideCategoryResponse = {
  message: string;
  propertyId?: string;
  category_id?: string;
};

export type UpdatePreferredVendorResponse = {
  vendors: ApiRankedVendor[];
  propertyId?: string;
  category_id?: string;
  sub_category_id?: string;
};

export type ResetToDefaultVendorResponse = {
  vendors: ApiRankedVendor[];
  propertyId?: string;
  sub_category_id?: string;
};

export type InitialStateType = {
  isFetchingProperties: boolean;
  isFetchingPropertyDetails: boolean;
  isUpdatingAccessInfo: boolean;
  data: {
    data: Array<string>;
  };
  source: {
    [propertyUuid: string]: PropertyListItem;
  };
  detailsSource: {
    [propertyUuid: string]: PropertyDetails;
  };
  residentsByProperty: {
    [propertyUuid: string]: PropertyResidentsState;
  };
  serviceRequestsByProperty: {
    [propertyUuid: string]: PropertyServiceRequestsState;
  };
  accessCodeHistoryByProperty: {
    [propertyUuid: string]: AccessCodeHistoryState;
  };
  mechanicalsByProperty: {
    [propertyUuid: string]: PropertyMechanicalsState;
  };
  moreDetailsByProperty: {
    [propertyUuid: string]: PropertyMoreDetailsState;
  };
  /**
   * Preferred Vendors tab state. Single flat instance — only one property detail
   * page is open at a time, so this is not keyed by property.
   */
  preferredVendors: PropertyPreferredVendorsState;
  pagination: {
    nextCursor: string | null;
  };
  lastSyncedAt: string | null;
  unassignedPropertiesCount: number;
  currentPropertyDetailsTab: string;
  /**
   * Drives the "unassigned properties" filtered view. Hydrated once from the
   * `?market_assignment=` URL param when the page opens (see Properties/index),
   * then the param is stripped from the URL so a copied link stays a plain
   * properties view. Lives in state — not the URL — so it can't be shared.
   */
  marketAssignmentFilter: string | null;
};

export const propertiesInitialState: InitialStateType = {
  isFetchingProperties: false,
  isFetchingPropertyDetails: true,
  isUpdatingAccessInfo: false,
  data: {
    data: [],
  },
  source: {},
  detailsSource: {},
  residentsByProperty: {},
  serviceRequestsByProperty: {},
  accessCodeHistoryByProperty: {},
  mechanicalsByProperty: {},
  moreDetailsByProperty: {},
  preferredVendors: createPreferredVendorsState(),
  pagination: {
    nextCursor: null,
  },
  lastSyncedAt: null,
  unassignedPropertiesCount: 0,
  currentPropertyDetailsTab: 'overview',
  marketAssignmentFilter: null,
};
